import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, formatApiError } from "../../lib/api";
import { SERVICE_ICONS, getIcon } from "../../lib/icons";

const DEFAULTS = { text: "", textarea: "", number: 0, bool: false, lines: [], icon: "Sparkles" };

const Field = ({ field, value, onChange, testId }) => {
  const { type, label } = field;
  const wrap = (el) => (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.06em] text-ink/70">{label}</label>
      {el}
    </div>
  );
  if (type === "bool")
    return (
      <div className="flex items-center justify-between rounded-xl border border-ink/10 p-3">
        <span className="text-sm font-semibold">{label}</span>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />
      </div>
    );
  if (type === "textarea") return wrap(<Textarea rows={4} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />);
  if (type === "lines") return wrap(<Textarea rows={5} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} data-testid={testId} />);
  if (type === "number") return wrap(<Input type="number" value={value} onChange={(e) => onChange(Number(e.target.value) || 0)} data-testid={testId} />);
  if (type === "icon")
    return wrap(
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.keys(SERVICE_ICONS).map((k) => {
            const I = SERVICE_ICONS[k];
            return (
              <SelectItem key={k} value={k}>
                <span className="flex items-center gap-2"><I className="h-4 w-4" /> {k}</span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>,
    );
  return wrap(<Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />);
};

const blank = (fields) => Object.fromEntries(fields.map((f) => [f.key, f.key === "active" ? true : DEFAULTS[f.type]]));

export const CollectionPanel = ({ name, title, description, fields, titleKey, subtitleKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/${name}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [name]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const payload = (item) => {
    const out = Object.fromEntries(fields.map((f) => [f.key, item[f.key]]));
    fields.filter((f) => f.type === "lines").forEach((f) => (out[f.key] = (out[f.key] || []).map((s) => s.trim()).filter(Boolean)));
    return out;
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/${name}/${editing.id}`, payload(editing));
      else await api.post(`/admin/${name}`, payload(editing));
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item, active) => {
    setItems((list) => list.map((x) => (x.id === item.id ? { ...x, active } : x)));
    try {
      await api.put(`/admin/${name}/${item.id}`, payload({ ...item, active }));
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar "${item[titleKey]}"?`)) return;
    try {
      await api.delete(`/admin/${name}/${item.id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid={`collection-${name}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">{title}</h2>
          {description && <p className="mt-1 text-sm text-ink/70">{description}</p>}
        </div>
        <button onClick={() => setEditing({ ...blank(fields), order: items.length })} className="btn-ink !px-6 !py-3 !text-sm" data-testid={`${name}-new-btn`}>
          <Plus className="h-4 w-4" /> Nuevo
        </button>
      </div>
      <div className="mt-6 space-y-3">
        {loading && <Loader2 className="h-6 w-6 animate-spin text-ink/60" />}
        {!loading && !items.length && <p className="rounded-2xl border border-dashed border-ink/20 p-8 text-center text-sm text-ink/70" data-testid={`${name}-empty`}>Aún no hay elementos. Crea el primero.</p>}
        {items.map((item, i) => {
          const Icon = name === "services" ? getIcon(item.icon) : null;
          return (
            <div key={item.id} className="flex flex-col gap-4 rounded-2xl border border-ink/10 bg-white p-4 sm:flex-row sm:items-center sm:p-5" data-testid={`${name}-row-${i}`}>
              {Icon && <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-leaf/25"><Icon className="h-5 w-5" /></span>}
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{item[titleKey]}</p>
                {subtitleKey && <p className="line-clamp-1 text-sm text-ink/70">{item[subtitleKey]}</p>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-ink/70">{item.active ? "Activo" : "Inactivo"}</span>
                <Switch checked={!!item.active} onCheckedChange={(v) => toggle(item, v)} data-testid={`${name}-toggle-${i}`} />
                <button onClick={() => setEditing({ ...item })} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-ink/5" aria-label="Editar" data-testid={`${name}-edit-${i}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(item)} className="grid h-9 w-9 place-items-center rounded-full border border-coral/30 text-coral hover:bg-coral/10" aria-label="Eliminar" data-testid={`${name}-delete-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid={`${name}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing?.id ? "Editar" : "Nuevo"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {fields.map((f) => (
                <Field key={f.key} field={f} value={editing[f.key]} onChange={(v) => setEditing((e) => ({ ...e, [f.key]: v }))} testId={`${name}-field-${f.key}`} />
              ))}
              <button onClick={save} disabled={saving} className="btn-leaf w-full !py-3.5 disabled:opacity-60" data-testid={`${name}-save-btn`}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
